import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SearchSvg from 'components/_common/Icons/SVGIcons/SearchSvg';
import AdminAPI from 'config/api/AdminAPI';
import { encryptText, placeParamsToURL } from 'helpers/CommonHelper';
import { hasUserAccessFunctionality, permissionSlugs } from 'helpers/PermissionHelper';
import { t } from 'i18next';
import debounce from 'lodash.debounce';
import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, InputGroup, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  adminCustomerRoutes,
  adminGunsForSaleRoutes,
  adminMainRoutes,
} from 'routes/admin/AdminRoutes';
import APIService from 'services/api.service';

import '../../../../assets/scss/globalSearchBar.scss';
import type { GlobalSearchItem } from 'types/GlobalSearchItem';

const BlogItem = lazy(() => import('./Section/BlogItem'));
const CategoryItem = lazy(() => import('./Section/CategoryItem'));
const FflItem = lazy(() => import('./Section/FflItem'));
const GunForSaleItem = lazy(() => import('./Section/GunForSaleItem'));
const ProductItem = lazy(() => import('./Section/ProductItem'));
const UserItem = lazy(() => import('./Section/UserItem'));

interface CategoryResult {
  total: number;
  page: number;
  limit: number;
  has_more: boolean;
  results: GlobalSearchItem[];
}

interface SearchResponseData {
  products?: CategoryResult;
  ffls?: CategoryResult;
  blogs?: CategoryResult;
  categories?: CategoryResult;
  users?: CategoryResult;
  guns_for_sale?: CategoryResult;
}

interface GlobalSearchBarProps {
  isDark?: boolean;
}

const GlobalSearchBar = ({ isDark = false }: GlobalSearchBarProps) => {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [results, setResults] = useState<SearchResponseData | null>(null);

  const canViewProducts = hasUserAccessFunctionality([
    permissionSlugs.product_list,
    permissionSlugs.edit_product,
    permissionSlugs.product_detail,
  ]);
  const canViewFFLs = hasUserAccessFunctionality([
    permissionSlugs.ffl_list,
    permissionSlugs.edit_ffl,
    permissionSlugs.ffl_detail,
  ]);
  const canViewBlogs = hasUserAccessFunctionality([
    permissionSlugs.blog_list,
    permissionSlugs.edit_blog,
    permissionSlugs.blog_detail,
  ]);
  const canViewCategories = hasUserAccessFunctionality([
    permissionSlugs.product_category_list,
    permissionSlugs.edit_product_category,
  ]);
  const canViewUsers = hasUserAccessFunctionality([
    permissionSlugs.employee_list,
    permissionSlugs.edit_employee,
  ]);
  const canViewGunsForSale = hasUserAccessFunctionality([
    permissionSlugs.active_gun_list,
    permissionSlugs.active_gun_detail,
  ]);

  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [wrapperRef]);

  // Handle Escape key
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setShowDropdown(false);
      }
    }
    if (showDropdown) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showDropdown]);

  const performSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setResults(null);
      setIsLoading(false);
      setShowDropdown(false);
      return;
    }

    setIsLoading(true);
    try {
      const response: any = await APIService.submitData(AdminAPI.globalSearch.url, {
        search: query,
        page: 1,
        limit: 5,
      });

      if (response && response.status) {
        setResults(response.data);
      } else {
        setResults(null);
      }
      setShowDropdown(true);
    } catch (error) {
      console.error('Search error:', error);
      setResults(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const debouncedSearch = useMemo(
    () =>
      debounce((query: string) => {
        performSearch(query);
      }, 300),
    [performSearch]
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setSearchValue(val);
      debouncedSearch(val);
    },
    [debouncedSearch]
  );

  const handleClear = useCallback(() => {
    setSearchValue('');
    setResults(null);
    setShowDropdown(false);
    debouncedSearch.cancel();
  }, [debouncedSearch]);

  const hasResults = useMemo(() => {
    if (!results) return false;
    return Object.values(results).some((cat) => cat && cat.results && cat.results.length > 0);
  }, [results]);

  const handleResultClick = (routePath: string, params: Record<string, string> = {}) => {
    setIsLoading(true);
    handleClear();
    navigate(placeParamsToURL(routePath, params));
    setIsLoading(false);
  };

  return (
    <div className="global-search-container position-relative w-100" ref={wrapperRef}>
      <InputGroup
        className={`align-items-center border search-input ${isDark ? 'bg-white' : 'searchbar-bg me-4'}`}
      >
        <Form.Control
          type="text"
          placeholder={t('search_text')}
          className={`border-0 shadow-none ${isDark ? 'bg-white' : ''}`}
          value={searchValue}
          onChange={handleInputChange}
          onFocus={() => {
            if (searchValue.trim()) setShowDropdown(true);
          }}
        />
        <InputGroup.Text className={`border-0 search-icon ${isDark ? 'bg-white' : ''}`}>
          {isLoading ? (
            <Spinner animation="border" size="sm" />
          ) : searchValue ? (
            <button
              className="bg-transparent border-0 p-0 m-0 d-flex align-items-center justify-content-center"
              onClick={handleClear}
              type="button"
              style={{ width: '18', height: '18' }}
            >
              <FontAwesomeIcon icon={faTimes} height={18} width={18} />
            </button>
          ) : (
            <SearchSvg height={18} width={18} />
          )}
        </InputGroup.Text>
      </InputGroup>

      {showDropdown && searchValue.trim() && (
        <div
          className={`search-dropdown position-absolute top-100 start-0 w-100 bg-white border rounded shadow mt-1 overflow-auto ${isDark ? 'dark-theme' : ''}`}
        >
          {isLoading && !results ? (
            <div className="search-loading p-4 text-center text-secondary">
              <Spinner animation="border" variant="secondary" />
              <div className="mt-2">{t('loading')}...</div>
            </div>
          ) : !hasResults ? (
            <div className="search-no-results p-4 text-center text-secondary">
              {t('no_results_found')}
            </div>
          ) : (
            <div className="search-results-list">
              <Suspense
                fallback={
                  <div className="search-loading p-4 text-center text-secondary">
                    <Spinner animation="border" variant="secondary" />
                    <div className="mt-2">{t('loading')}...</div>
                  </div>
                }
              >
                {canViewProducts && (results?.products?.results?.length ?? 0) > 0 && (
                  <div className="search-category border-bottom">
                    <div className="category-header px-3 py-1 bg-light fw-bold text-secondary text-uppercase small">
                      Products
                    </div>
                    <ul className="category-items list-unstyled m-0 p-0">
                      {results?.products?.results?.map((item) => (
                        <ProductItem
                          key={`product-${item.id}`}
                          item={item}
                          onClick={() =>
                            handleResultClick(adminMainRoutes.productDetail.path, {
                              id: encryptText(String(item.id)),
                            })
                          }
                        />
                      ))}
                    </ul>
                  </div>
                )}
                {(results?.ffls?.results?.length ?? 0) > 0 && canViewFFLs && (
                  <div className="search-category border-bottom">
                    <div className="category-header px-3 py-1 bg-light fw-bold text-secondary text-uppercase small">
                      FFLs
                    </div>
                    <ul className="category-items list-unstyled m-0 p-0">
                      {results?.ffls?.results?.map((item) => (
                        <FflItem
                          key={`ffl-${item.id}`}
                          item={item}
                          onClick={() =>
                            handleResultClick(adminMainRoutes.fflDetail.path, {
                              slug: encryptText(String(item.id)),
                            })
                          }
                        />
                      ))}
                    </ul>
                  </div>
                )}
                {(results?.blogs?.results?.length ?? 0) > 0 && canViewBlogs && (
                  <div className="search-category border-bottom">
                    <div className="category-header px-3 py-1 bg-light fw-bold text-secondary text-uppercase small">
                      Blogs
                    </div>
                    <ul className="category-items list-unstyled m-0 p-0">
                      {results?.blogs?.results?.map((item) => (
                        <BlogItem
                          key={`blog-${item.id}`}
                          item={item}
                          onClick={() =>
                            handleResultClick(adminMainRoutes.blogDetail.path, {
                              slug: encryptText(String(item.id)),
                            })
                          }
                        />
                      ))}
                    </ul>
                  </div>
                )}
                {canViewCategories && (results?.categories?.results?.length ?? 0) > 0 && (
                  <div className="search-category border-bottom">
                    <div className="category-header px-3 py-1 bg-light fw-bold text-secondary text-uppercase small">
                      Categories
                    </div>
                    <ul className="category-items list-unstyled m-0 p-0">
                      {results?.categories?.results?.map((item) => (
                        <CategoryItem
                          key={`cat-${item.id}`}
                          item={item}
                          onClick={() =>
                            handleResultClick(
                              `${adminMainRoutes.categories.path}?search=${encryptText(item.name || '')}`
                            )
                          }
                        />
                      ))}
                    </ul>
                  </div>
                )}
                {(results?.users?.results?.length ?? 0) > 0 && canViewUsers && (
                  <div className="search-category border-bottom">
                    <div className="category-header px-3 py-1 bg-light fw-bold text-secondary text-uppercase small">
                      Users
                    </div>
                    <ul className="category-items list-unstyled m-0 p-0">
                      {results?.users?.results?.map((item) => (
                        <UserItem
                          key={`user-${item.id}`}
                          item={item}
                          onClick={() => {
                            const userName =
                              item.name ||
                              `${item.first_name || ''} ${item.last_name || ''}`.trim() ||
                              '';
                            handleResultClick(
                              `${adminCustomerRoutes.customerList.path}?search=${encryptText(userName)}`
                            );
                          }}
                        />
                      ))}
                    </ul>
                  </div>
                )}
                {canViewGunsForSale && (results?.guns_for_sale?.results?.length ?? 0) > 0 && (
                  <div className="search-category border-bottom">
                    <div className="category-header px-3 py-1 bg-light fw-bold text-secondary text-uppercase small">
                      Guns for Sale
                    </div>
                    <ul className="category-items list-unstyled m-0 p-0">
                      {results?.guns_for_sale?.results?.map((item) => (
                        <GunForSaleItem
                          key={`gun-${item.id}`}
                          item={item}
                          onClick={() =>
                            handleResultClick(adminGunsForSaleRoutes.activeGunList.path)
                          }
                        />
                      ))}
                    </ul>
                  </div>
                )}
              </Suspense>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearchBar;
